// =====================================================================
// src/components/intl-acca/TopbarTournamentSwitcher.tsx
// =====================================================================
// Dropdown that lives in the AppShell topbar — lets the operator switch
// the active tournament context across the whole module. Pure
// presentational; host passes the list + the active id + a callback.
//
// Mount inside src/components/layout/Topbar.tsx, OR conditionally
// render only when the route prefix is "/intl-acca/". See
// INTEGRATION_PATCH.md.

import { useEffect, useRef, useState } from "react";
import type { IntlAccaTournament } from "../../db/intl-acca-types";

interface TopbarTournamentSwitcherProps {
  tournaments: IntlAccaTournament[];
  activeTournamentId: string | null;
  onSelect: (id: string) => void;
  /** "View calendar →" entry at the bottom of the menu. */
  onOpenCalendar?: () => void;
}

const STATUS_COLOR: Record<IntlAccaTournament["status"], string> = {
  ACTIVE:           "var(--pos)",
  PRE_TOURNAMENT:   "var(--accent)",
  POST_TOURNAMENT:  "var(--info)",
  ARCHIVED:         "var(--text-tertiary)",
};

export function TopbarTournamentSwitcher({
  tournaments,
  activeTournamentId,
  onSelect,
  onOpenCalendar,
}: TopbarTournamentSwitcherProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const active = tournaments.find((t) => t.id === activeTournamentId) ?? tournaments[0];

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!active) return null;

  return (
    <div className="ia-tswitch" ref={rootRef}>
      <button
        type="button"
        className="ia-tswitch-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontFamily: "var(--sz-font-display)",
              fontSize: 16,
              lineHeight: 1,
              letterSpacing: "0.04em",
              color: "var(--text)",
            }}
          >
            {active.name}
          </div>
          <div
            className="ia-mono"
            style={{
              fontSize: 9,
              color: "var(--text-tertiary)",
              letterSpacing: "0.2em",
              marginTop: 3,
              textTransform: "uppercase",
            }}
          >
            {active.status.replace("_", " ")} · {active.region}
          </div>
        </div>
        <span
          aria-hidden
          className="ia-mono"
          style={{ fontSize: 11, color: "var(--text-tertiary)" }}
        >
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open ? (
        <div className="ia-tswitch-menu" role="listbox">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-faint)",
            }}
          >
            <div
              className="ia-mono"
              style={{
                fontSize: 9.5,
                color: "var(--text-tertiary)",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
              }}
            >
              Switch tournament · {tournaments.length} windows
            </div>
          </div>
          {tournaments.map((t) => {
            const isActive = t.id === active.id;
            return (
              <button
                key={t.id}
                type="button"
                className={`ia-tswitch-item${isActive ? " active" : ""}`}
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onSelect(t.id);
                  setOpen(false);
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: STATUS_COLOR[t.status],
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "var(--sz-body-sm)",
                          color: isActive ? "var(--accent)" : "var(--text)",
                          fontWeight: 500,
                        }}
                      >
                        {t.name}
                      </span>
                      {isActive ? (
                        <span
                          className="ia-mono"
                          style={{
                            fontSize: 9,
                            color: "var(--accent)",
                            letterSpacing: "0.2em",
                          }}
                        >
                          ● ACTIVE
                        </span>
                      ) : null}
                    </div>
                    <div
                      className="ia-mono"
                      style={{
                        fontSize: 9.5,
                        color: STATUS_COLOR[t.status],
                        letterSpacing: "0.18em",
                        marginTop: 3,
                        textTransform: "uppercase",
                      }}
                    >
                      {t.status.replace("_", " ")}
                      <span style={{ color: "var(--text-tertiary)", marginLeft: 6 }}>
                        · {t.region}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {onOpenCalendar ? (
            <button
              type="button"
              onClick={() => {
                onOpenCalendar();
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "center",
                background: "transparent",
                border: "none",
                padding: "14px 16px",
                fontFamily: "var(--sz-font-mono)",
                fontSize: 11,
                color: "var(--accent)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              View full calendar →
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: tournaments } = await sb.from('intl_acca_tournaments').select('*');
//
// Active id is host-managed (e.g. URL param, Zustand store, localStorage).
// onSelect: persist + navigate to /intl-acca/:id.
